import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  Grid,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { FormData } from '../CreateFormDialog';
import { wizardOutlinedButtonSx } from '../wizardStyles';
import { SingleFormArtwork, MultiStepFormArtwork } from '../../../components/shared/Icons';
import { ADMIN_COLORS } from '../../../theme';

interface BasicDetailsAndTypeProps {
  onNext: (data: Partial<FormData>) => void;
  onCancel: () => void;
  initialData: FormData;
  loading?: boolean;
  submitEveryStep: boolean;
  onToggleSubmitEveryStep: (v: boolean) => void;
}

const formTypes = [
  {
    key: 'single' as const,
    label: 'Single page form',
    description: 'All fields laid out on one page, submitted at once.',
    bg: ADMIN_COLORS.accentSoft,
    art: (accent: string) => <SingleFormArtwork accent={accent} />,
  },
  {
    key: 'multistep' as const,
    label: 'Multi-step form',
    description: 'Fields split across steps, with progress as users move through.',
    bg: ADMIN_COLORS.warningSoft,
    art: (accent: string) => <MultiStepFormArtwork accent={accent} />,
  },
];

export const BasicDetailsAndType: React.FC<BasicDetailsAndTypeProps> = ({
  onNext,
  onCancel,
  initialData,
  loading = false,
  submitEveryStep,
  onToggleSubmitEveryStep,
}) => {
  const [formName, setFormName] = useState(initialData.formName || '');
  const [nameError, setNameError] = useState(initialData.formNameError || '');
  const [selectedType, setSelectedType] = useState<'single' | 'multistep' | null>(
    initialData.formType || null,
  );

  useEffect(() => {
    setNameError(initialData.formNameError || '');
  }, [initialData.formNameError]);

  const canSubmit = !!formName.trim() && !!selectedType;
  const accent = '#4F46E5';

  const handleSubmit = () => {
    if (loading || !canSubmit) return;
    if (!formName.trim()) {
      setNameError('Form name is required');
      return;
    }
    setNameError('');
    onNext({
      formName: formName.trim(),
      formNameError: undefined,
      formType: selectedType!,
      submitEveryStep: selectedType === 'multistep' ? submitEveryStep : false,
    });
  };

  return (
    <Box>
      <Typography
        sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary', mb: 1 }}
      >
        Form name <Box component="span" sx={{ color: '#E11D48' }}>*</Box>
      </Typography>
      <TextField
        fullWidth
        variant="outlined"
        value={formName}
        onChange={(e) => {
          setFormName(e.target.value);
          if (nameError) setNameError('');
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSubmit();
        }}
        error={!!nameError}
        helperText={nameError}
        placeholder="e.g. Client Details"
        autoFocus
        disabled={loading}
        sx={{
          mb: 3.5,
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: ADMIN_COLORS.surface,
            '& fieldset': { borderColor: ADMIN_COLORS.border },
            '&:hover fieldset': { borderColor: ADMIN_COLORS.borderStrong },
          },
          '& .MuiOutlinedInput-input': {
            py: 1.25,
            fontSize: '0.875rem',
            '&::placeholder': { color: ADMIN_COLORS.textFaint, opacity: 1 },
          },
        }}
      />

      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
          How should this form work?
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
          You can change this later from the form settings.
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: selectedType === 'multistep' ? 2 : 3.5 }}>
        {formTypes.map((formType) => {
          const isSelected = selectedType === formType.key;
          return (
            <Grid size={{ xs: 12, sm: 6 }} key={formType.key}>
              <Box
                onClick={() => !loading && setSelectedType(formType.key)}
                sx={{
                  position: 'relative',
                  cursor: loading ? 'default' : 'pointer',
                  height: '100%',
                  border: '1.5px solid',
                  borderColor: isSelected ? ADMIN_COLORS.accent : ADMIN_COLORS.border,
                  borderRadius: '14px',
                  overflow: 'hidden',
                  bgcolor: ADMIN_COLORS.surface,
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                  boxShadow: isSelected
                    ? `0 0 0 3px ${ADMIN_COLORS.accentRing}`
                    : '0 1px 2px rgba(16, 24, 40, 0.04)',
                  '&:hover': loading ? {} : { borderColor: ADMIN_COLORS.accent },
                }}
              >
                {isSelected && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: '#10B981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                      boxShadow: '0 2px 6px rgba(16, 185, 129, 0.35)',
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />
                  </Box>
                )}
                <Box sx={{ p: 2, pb: 1.5, bgcolor: formType.bg }}>
                  {formType.art(accent)}
                </Box>
                <Box sx={{ p: 2, pt: 1.75 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 0.5 }}>
                    {formType.label}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.5 }}>
                    {formType.description}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {selectedType === 'multistep' && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!submitEveryStep}
                onChange={(e) => onToggleSubmitEveryStep(e.target.checked)}
                color="primary"
              />
            }
            label="Submit at every step"
          />
        </Box>
      )}

      <Stack direction="row" spacing={1.5} justifyContent="flex-end">
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
          sx={wizardOutlinedButtonSx}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || loading}
          startIcon={
            loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : undefined
          }
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            px: 4,
            py: 0.75,
            fontWeight: 600,
            boxShadow: 'none',
            backgroundColor: ADMIN_COLORS.accent,
            '&:hover': { backgroundColor: ADMIN_COLORS.accentHover, boxShadow: 'none' },
            '&.Mui-disabled': { backgroundColor: ADMIN_COLORS.segmentBg, color: ADMIN_COLORS.textFaint },
          }}
        >
          {loading ? 'Saving...' : 'Continue'}
        </Button>
      </Stack>
    </Box>
  );
};
